import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { AgGridReact } from 'ag-grid-react';
import React, { useCallback, useEffect, useRef, useState } from "react";
import { connect, useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { Card, CardBody, Col, Input, Row } from "reactstrap";
import * as XLSX from 'xlsx';
import StyledButton from '../../components/Common/StyledButton';
import useAuth from '../../hooks/useAuth';
import RequireUserType from '../../routes/middleware/requireUserType';
import { getOrdersReq } from "../../service/orderService";
import { changePreloader } from "../../store/actions";
import { setBreadcrumbItems } from "../../store/Breadcrumb/actions";
import { MODULES_ENUM, PERMISSIONS_ENUM, USER_TYPES_ENUM } from '../../utility/constants';
import { formatDate } from '../../utility/formatDate';
import { formatNumberWithCommasAndDecimal } from "../Invoices/invoiceUtil";
import CircleRenderer from "./CircleRenderer";
import DropdownMenuBtn from "./DropdownMenuBtn";
import OrderStatusRenderer from "./OrderStatusRenderer";
import './styles/AllOrders.scss';

const AllOrders = (props) => {
  document.title = "All Orders";
  let dispatch = useDispatch();
  let navigate = useNavigate();
  const effectCalled = useRef(false);
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const startDate = params.get("startDate");
  const endDate = params.get("endDate");
  const status = params.get("status")

  const autoSizeStrategy = {
    type: 'fitGridWidth'
  };
  // enables pagination in the grid
  const pagination = true;
  const { auth } = useAuth();

  // sets 10 rows per page (default is 100)
  // allows the user to select the page size from a predefined list of page sizes
  const paginationPageSizeSelector = [25, 50, 100];

  const [rowData, setRowData] = useState([]);
  const [paginationPageSize, setPaginationPageSize] = useState(200);
  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [sortBody, setSortBody] = useState({
    key: 'date',
    order: "D"
  });
  const [isExporting, setIsExporting] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [orderStatus, setOrderStatus] = useState(status || "");
  const [date, setDate] = useState(() => {
    if (startDate && endDate) {
      return {
        start: startDate,
        end: endDate,
      };
    }
    return null;
  });


  const parseDateToISOStart = (dateStr) => {
    if (!dateStr) return null;

    if (dateStr.includes('-')) {
      return new Date(`${dateStr}T00:00:00.000Z`).toISOString();
    }

    if (dateStr.includes('/')) {
      const [day, month, year] = dateStr.split('/');
      return new Date(`${year}-${month}-${day}T00:00:00.000Z`).toISOString();
    }

    return null;
  };

  const parseDateToISOEnd = (dateStr) => {
    if (!dateStr) return null;

    if (dateStr.includes('-')) {
      return new Date(`${dateStr}T23:59:59.999Z`).toISOString();
    }

    if (dateStr.includes('/')) {
      const [day, month, year] = dateStr.split('/');
      return new Date(`${year}-${month}-${day}T23:59:59.999Z`).toISOString();
    }

    return null;
  };

  const redirectToViewPage = (id) => {
    let path = `/order/${id.salesorder_id}`;
    setTimeout(() => {
      navigate(path, id);
    }, 300);
  }

  const handleViewClick = (id) => {
    redirectToViewPage(id);
  }

  const onGridReady = useCallback((params) => {
    gridRef.current.api.sizeColumnsToFit();
    gridRef.current.api.autoSizeColumns(['order_status', 'invoiced_status', 'paid_status', 'shipped_status']);
  }, []);

  useEffect(() => {
    props.setBreadcrumbItems('Ongoing Orders', breadcrumbItems);
    const body = {
      page: page,
      limit: paginationPageSize,
    }
    if (searchValue) {
      body.search_text = searchValue;
    }
    if (sortBody) {
      body.sort = sortBody;
    }
    if (orderStatus) {
      body.order_status = orderStatus
    }
    const startDate = date?.start;
    const endDate = date?.end;

    if (startDate) {
      body.startDate = startDate;
    }

    if (endDate) {
      body.endDate = endDate;
    }
    getListOfRowData(body);
  }, [page, paginationPageSize, searchValue, sortBody, orderStatus, startDate, endDate]);

  const getListOfRowData = useCallback(async (body) => {
    if (rowData[(page - 1) * paginationPageSize]) {
      return;
    }
    dispatch(changePreloader(true));

    try {
      // const body = {
      //   page: page,
      //   limit: paginationPageSize,
      // }

      // if(searchValue) {
      //   body.search_text = searchValue;
      // }
      const response = await getOrdersReq(body);


      const emptyObjects = Array.from({ length: paginationPageSize }, () => (null));
      let filledRows;

      if (response.length < paginationPageSize) {
        filledRows = [...response];
      } else {
        filledRows = [...response, ...emptyObjects];
      }

      const newData = [...rowData];
      newData.splice((page - 1) * paginationPageSize, paginationPageSize, ...filledRows);
      setRowData(newData);

    } catch (error) {
      console.error("Error fetching purchase orders:", error);
    } finally {
      dispatch(changePreloader(false));
    }
  }, [page, paginationPageSize, searchValue]);

  const redirectToEditPage = (id) => {
    let path = "/edit-item";
    setTimeout(() => {
      navigate(path, id);
    }, 300);

  }
  const handleDeleteResponse = (response) => {
    if (response.success === true) {
      notify("Success", response.message);
    } else {
      notify("Error", response.message);
    }
    getListOfRowData();
  }
  const handleEditClick = (id) => {
    redirectToEditPage(id);
  }
  const onPaginationChanged = useCallback((event) => {
    // Workaround for bug in events order
    let pageSize = gridRef.current.api.paginationGetPageSize();
    setPaginationPageSize(pageSize);

    if (pageSize !== paginationPageSize) {
      setPaginationPageSize(pageSize);
    }
    const newPage = gridRef.current.api.paginationGetCurrentPage() + 1;

    if (page !== newPage) {
      setPage(newPage);
    }
  }, []);

  const handleInputChange = (event) => {
    setInputValue(event.target.value);
  }

  const handleSearch = (event) => {
    setSearchValue(event.target.value);
    setPage(1);
    setRowData([]);
  }

  const gridRef = useRef();

  const headerTemplate = (props) => {
    const { handleSort, data } = props
    return (
      <button onClick={() => handleSort(data, sortBody)} style={{ background: 'transparent', border: 'none' }}>
        <span style={{ fontWeight: 600 }}>
          {data?.displayName}&nbsp;
          {sortBody?.key === data?.column.userProvidedColDef.field ? <i className={sortBody?.order === "A" ? "mdi mdi-arrow-up" : "mdi mdi-arrow-down"}></i> : ''}
        </span>
      </button>
    )
  }

  const handleSort = (data, sortBody) => {
    if (data.column.userProvidedColDef.field === sortBody?.key) {
      setSortBody({ ...sortBody, order: sortBody?.order === "A" ? "D" : "A" })
    } else {
      setSortBody({
        key: data.column.userProvidedColDef.field,
        order: "D"
      })
    }
    setPage(1)
    setRowData([])
  }

  const breadcrumbItems = [
    { title: "Dashboard", link: "/dashboard" },
    { title: "Ongoing Orders", link: "#" },
  ];

  const agRowData = [
    {
      salesorder_id: "2859757000291305672",
      customer_name: "Lakshya Digital Private Limited",
      date: "2024-03-18",
      total: 3425.54,
      order_status: "closed",
      invoiced_status: "invoiced",
      paid_status: "unpaid",
      status: "invoiced",
    }
  ]

  const columnDefs = [
    {
      headerName: "Order Date", field: "date",
      floatingFilterComponentParams: { suppressFilterButton: true },
      tooltipValueGetter: (p) => p.value, headerTooltip: "Order Date",
      cellRenderer: (props) => {
        if (props.value) {
          let date = new Date(props.value);
          return <>{formatDate(date)}</>;
        }
      },
      sortable: false, minWidth: 110,
      headerComponent: headerTemplate,
      headerComponentParams: (props) => ({
        handleSort: handleSort,
        data: props,
        sortBody,
      })
    },
    {
      headerName: "SO No.", field: "salesorder_number",
      floatingFilterComponentParams: { suppressFilterButton: true },
      tooltipValueGetter: (p) => p.value, headerTooltip: "SO No.",
      sortable: false, minWidth: 140,
      headerComponent: headerTemplate,
      headerComponentParams: (props) => ({
        handleSort: handleSort,
        data: props,
        sortBody,
      })
    },
    {
      headerName: "PO No.", field: "cf_po_no",
      floatingFilterComponentParams: { suppressFilterButton: true },
      tooltipValueGetter: (p) => p.value, headerTooltip: "PO No.",
      sortable: false, minWidth: 140,
      headerComponent: headerTemplate,
      headerComponentParams: (props) => ({
        handleSort: handleSort,
        data: props,
        sortBody,
      })
    },
    {
      headerName: "Client", field: "customer_name",
      tooltipField: "customer_name",
      floatingFilterComponentParams: { suppressFilterButton: true },
      tooltipValueGetter: (p) => p.value,
      headerTooltip: "Client",
      sortable: false, minWidth: 150,
      headerComponent: headerTemplate,
      headerComponentParams: (props) => ({
        handleSort: handleSort,
        data: props,
        sortBody,
      })
    },
    {
      headerName: "Status", field: "order_status",
      cellRenderer: (params) => (
        <OrderStatusRenderer value={params.value} type="ongoing" />

      ),
      cellStyle: {
        textAlign: "left",
        display: "flex",
        justifyContent: "flex-start",
      },
      floatingFilterComponentParams: { suppressFilterButton: true },
      tooltipValueGetter: (p) => p.value, headerTooltip: "Order Status",
      sortable: false, minWidth: 80
    },
    {
      headerName: "Amount", field: "total", width: 140,
      floatingFilterComponentParams: { suppressFilterButton: true },
      tooltipValueGetter: (p) => p.value, headerTooltip: "Total Amount",
      valueFormatter: params => formatNumberWithCommasAndDecimal(params.value),
      sortable: false,
      headerComponent: headerTemplate,
      headerComponentParams: (props) => ({
        handleSort: handleSort,
        data: props,
        sortBody,
      })
    },
    {
      headerName: "Invoice", field: "invoice_number", cellRenderer: CircleRenderer,
      floatingFilterComponentParams: { suppressFilterButton: true },
      tooltipValueGetter: (p) => p.value, headerTooltip: "Invoice",
      sortable: false, width: 90
    },

    {
      headerName: "Packages", field: "shipped_status", cellRenderer: CircleRenderer,
      floatingFilterComponentParams: { suppressFilterButton: true },
      tooltipValueGetter: (p) => p.value,
      headerTooltip: "Packages",
      sortable: false, width: 100

    },
    {
      headerName: "Action", field: "action", sortable: false, width: 80,
      cellClass: "actions-button-cell",
      cellRenderer: DropdownMenuBtn,
      cellRendererParams: {
        handleResponse: handleDeleteResponse,
        handleEditClick: handleEditClick,
        handleViewClick: handleViewClick,
      }, suppressMenu: true, floatingFilterComponentParams: { suppressFilterButton: true },
      tooltipValueGetter: (p) => p.value, headerTooltip: "Actions"
    }
  ];


  const notify = (type, message) => {
    if (type === "Error") {
      toast.error(message, {
        position: "top-center",
        theme: "colored",
      })
    } else {
      toast.success(message, {
        position: "top-center",
        theme: "colored",
      })
    }
  }

  const clientColumnDefs = columnDefs.filter(colDef => colDef.headerName !== "Client")
  const clientUserColumnDefs = clientColumnDefs.filter(colDef => colDef.headerName !== "Amount")

  const downloadOrdersExcel = async () => {
    try {
      setIsExporting(true);
      const body = {
        page: 1,
        limit: 200,
        sort: {
          key: 'date',
          order: 'A',
        },
      };

      if (new Date(body.startDate) > new Date(body.endDate)) {
        toast.error('From Date cannot be after To Date');
        return;
      }

      if (fromDate && toDate) {
        body.startDate = parseDateToISOStart(fromDate);
        body.endDate = parseDateToISOEnd(toDate);

        if (!body.startDate || !body.endDate) {
          toast.error('Invalid date format');
          return;
        }
      }

      const data = await getOrdersReq(body);


      if (data && data.length > 0) {
        const flatOrders = data.map(order => ({
          Date: order.date,
          'SO No': order.salesorder_number,
          'PO No': order.cf_po_no,
          Client: order.customer_name,
          Quantity: order.quantity,
          Amount: order.total,
          Status: order.order_status,
        }));

        console.log(data)
        // Convert to worksheet
        const worksheet = XLSX.utils.json_to_sheet(flatOrders);

        // Create workbook and add worksheet
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Orders');

        // Download as Excel file
        XLSX.writeFile(workbook, 'OrdersList.xlsx');
        toast.success('Orders Exported Successfully!');
        setIsExporting(false);
      } else {
        console.error('Failed to fetch Orders:', data.error || data.message);
        setIsExporting(false);
      }
    } catch (error) {
      console.error('Error downloading Excel:', error);
      setIsExporting(false);
    }
  };

  useEffect(() => {
    const handler = (e) => {
      if (!e.target.closest('.position-relative')) {
        setExportOpen(false);
      }
    };

    if (exportOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [exportOpen]);

  return (
    <>
      <div className="all-items">
        <Row>
          <Col className="col-12">
            <Card>
              <CardBody>
                <div className="button-section">
                  <div className="button-right-section">
                    <div className="invoice-search-box">
                      <div className="search-box position-relative" style={{ width: auth.userType === USER_TYPES_ENUM.ADMIN ? '20rem' : '14rem' }}>
                        <Input
                          type="text"
                          value={inputValue}
                          onChange={handleInputChange}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              handleSearch(event);
                            }
                          }}
                          className="form-control rounded border"
                          placeholder={auth.userType === USER_TYPES_ENUM.ADMIN ? "Search by Client or Order Number" : "Search by Order Number"}
                        />
                        <i className="mdi mdi-magnify search-icon"></i>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <select
                        value={orderStatus}
                        onChange={(e) => setOrderStatus(e.target.value)}
                        className="px-4 py-1 rounded-md border border-gray-300 bg-white text-sm text-gray-700 outline-none transition duration-200
                      focus:border-blue-500 focus:ring-2 focus:ring-blue-200 hover:border-gray-400 cursor-pointer">
                        <option value="">All</option>
                        <option value="open">Packed</option>
                        <option value="draft">In Process</option>
                        <option value="closed">Completed</option>
                      </select>
                    </div>
                    <div className="position-relative">
                      <StyledButton
                        color={"primary"}
                        className={"w-md"}
                        onClick={() => setExportOpen(prev => !prev)}
                        isLoading={isExporting}
                      >
                        <div>
                          <i className="ion ion-md-cloud-download mx-2"></i>
                          Export
                        </div>
                      </StyledButton>


                      {exportOpen && (
                        <div
                          className="position-absolute bg-white border rounded shadow p-3"
                          style={{
                            right: 0,
                            top: '110%',
                            zIndex: 1000,
                            minWidth: '280px',
                          }}
                        >
                          <p className="fw-semibold mb-3">Export Orders</p>

                          <div className="mb-2">
                            <label className="form-label mb-1">From Date</label>
                            <Input
                              type="date"
                              value={fromDate}
                              onChange={(e) => setFromDate(e.target.value)}
                            />
                          </div>

                          <div className="mb-3">
                            <label className="form-label mb-1">To Date</label>
                            <Input
                              type="date"
                              value={toDate}
                              onChange={(e) => setToDate(e.target.value)}
                            />
                          </div>

                          <div className="d-flex justify-content-end gap-2">
                            <button
                              className="btn btn-light btn-sm"
                              onClick={() => {
                                setExportOpen(false);
                                setFromDate('');
                                setToDate('');
                              }}
                            >
                              Cancel
                            </button>

                            <button
                              className="btn btn-primary btn-sm"
                              disabled={!fromDate || !toDate}
                              onClick={() => {
                                if (new Date(fromDate) > new Date(toDate)) {
                                  toast.error('From Date cannot be after To Date');
                                  return;
                                }
                                setExportOpen(false);
                                downloadOrdersExcel();
                              }}
                            >
                              Apply
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div
                  className="ag-theme-quartz"
                  style={{
                    height: '500px',
                    width: '100%'
                  }}
                >
                  <RequireUserType userType={USER_TYPES_ENUM.ADMIN}>
                    <AgGridReact
                      ref={gridRef}
                      autoSizeStrategy={autoSizeStrategy}
                      columnDefs={columnDefs}
                      pagination={pagination}
                      paginationPageSize={paginationPageSize}
                      paginationPageSizeSelector={false}
                      rowData={rowData}
                      onPaginationChanged={onPaginationChanged}
                      reactiveCustomComponents
                      onGridReady={onGridReady}
                    >
                    </AgGridReact>
                  </RequireUserType>
                  <RequireUserType userType={USER_TYPES_ENUM.CLIENT}>
                    <AgGridReact
                      ref={gridRef}
                      autoSizeStrategy={autoSizeStrategy}
                      columnDefs={auth?.permissions?.some(
                        (p) =>
                          p.module === MODULES_ENUM.ORDERS &&
                          [PERMISSIONS_ENUM.READ, PERMISSIONS_ENUM.CREATE].every((perm) => p.operations.includes(perm))
                      )
                        ? clientColumnDefs
                        : auth?.permissions?.some(
                          (p) =>
                            p.module === MODULES_ENUM.ORDERS && p.operations.includes(PERMISSIONS_ENUM.READ)
                        )
                          ? clientUserColumnDefs
                          : []}
                      pagination={pagination}
                      paginationPageSize={paginationPageSize}
                      paginationPageSizeSelector={false}
                      rowData={rowData}
                      onPaginationChanged={onPaginationChanged}
                      reactiveCustomComponents
                      onGridReady={onGridReady}
                    >
                    </AgGridReact>
                  </RequireUserType>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  )
}

export default connect(null, { setBreadcrumbItems })(AllOrders);
